import type { CarrierQuote, TaxFee } from 'api/quote-marketplace';

export interface PremiumBreakdown {
  annualPremium: number;
  monthlyPremium: number;
  taxTotal: number;
  taxPercentage: number;
  monthlyTax: number;
}

// Calculate tax total from taxes array
export const calculateTaxTotal = (
  taxes: TaxFee[] | null | undefined,
): number => {
  if (!taxes) return 0;
  return taxes.reduce((sum, tax) => sum + (tax.amount || 0), 0);
};

// Get tax percentage from taxes array (uses first tax entry)
export const getTaxPercentage = (
  taxes: TaxFee[] | null | undefined,
): number => {
  return taxes?.[0]?.percentage_amount || 0;
};

// Calculate full premium breakdown from raw values
// Note: annualPremium already includes tax
export const calculatePremiumBreakdown = (
  annualPremium: string | number | null | undefined,
  taxes: TaxFee[] | null | undefined,
): PremiumBreakdown => {
  const annual =
    typeof annualPremium === 'string'
      ? parseFloat(annualPremium) || 0
      : annualPremium || 0;

  const taxTotal = calculateTaxTotal(taxes);
  const taxPercentage = getTaxPercentage(taxes);

  return {
    annualPremium: annual,
    monthlyPremium: annual / 12,
    taxTotal,
    taxPercentage,
    monthlyTax: taxTotal / 12,
  };
};

// Calculate premium breakdown from CarrierQuote object
export const calculateQuotePremiumBreakdown = (
  quote: CarrierQuote | undefined,
): PremiumBreakdown => {
  if (!quote) {
    return {
      annualPremium: 0,
      monthlyPremium: 0,
      taxTotal: 0,
      taxPercentage: 0,
      monthlyTax: 0,
    };
  }

  return calculatePremiumBreakdown(quote.annual_premium, quote.taxes);
};

// Legacy functions for backward compatibility
export const getTaxAmount = (quote: CarrierQuote | undefined): number => {
  return calculateTaxTotal(quote?.taxes);
};

export const getAnnualPremium = (quote: CarrierQuote | undefined): number => {
  if (!quote?.annual_premium) return 0;
  const annual = parseFloat(quote.annual_premium);
  return isNaN(annual) ? 0 : annual;
};

export const getMonthlyPremium = (quote: CarrierQuote | undefined): number => {
  return getAnnualPremium(quote) / 12;
};

export const getMonthlyTax = (quote: CarrierQuote | undefined): number => {
  return getTaxAmount(quote) / 12;
};
